import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete } from "@mui/material";
import AdvancedTable from "../../sharedComponents/AdvancedTable/AdvancedTable";
import {
    getParentTaskReport,
    getParentTasksByProject,
    getAllProject,
} from "../../services/reports.service";
import { getUser } from "../../services/user.service";
import { DatePicker } from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const ParentTaskReport = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [parentTasks, setParentTasks] = useState([]);
    const [selectedParentTask, setSelectedParentTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const initialMonth = dayjs();
    const [dateRange, setDateRange] = useState({
        startDate: initialMonth.startOf("month"),
        endDate: initialMonth,
    });
    const [data, setData] = useState([]);
    const [dateColumns, setDateColumns] = useState([]);

    // Fetch all projects
    useEffect(() => {
        getAllProject({ isEnabled: true }).then((res) => {
            const projectList = Array.isArray(res.data) ? res.data : [];
            setProjects(projectList);
        }).catch((error) => {
            console.error("Error fetching projects:", error);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    // Fetch all users
    useEffect(() => {
        getUser({ isEnabled: true }).then((res) => {
            const userList = Array.isArray(res.data?.data) ? res.data.data : [];
            setUsers([{ _id: "All", name: "All" }, ...userList]);
        }).catch((error) => {
            console.error("Error fetching users:", error);
        });
    }, []);

    // Fetch parent tasks when project is selected
    useEffect(() => {
        if (selectedProject && selectedProject._id) {
            setLoading(true);
            getParentTasksByProject(selectedProject._id)
                .then((res) => {
                    const parentTaskList = Array.isArray(res.data)
                        ? res.data
                        : [];
                    setParentTasks([
                        { _id: "All", title: "All", code: "All" },
                        ...parentTaskList,
                    ]);
                    setSelectedParentTask({ _id: "All", title: "All", code: "All" });
                })
                .catch((error) => {
                    console.error("Error fetching parent tasks:", error);
                    setParentTasks([{ _id: "All", title: "All", code: "All" }]);
                    setSelectedParentTask({ _id: "All", title: "All", code: "All" });
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setParentTasks([]);
            setSelectedParentTask(null);
            setData([]);
        }
    }, [selectedProject]);

    // Fetch report whenever filters change
    useEffect(() => {
        if (!selectedProject || !selectedProject._id) {
            setData([]);
            setLoading(false);
            return;
        }

        // Show loading immediately when filter changes
        setLoading(true);

        const abortController = new AbortController();
        let timeoutId;



        const fetchReport = async () => {
            try {
                const res = await getParentTaskReport({
                    startDate: dateRange.startDate
                        ? dateRange.startDate.startOf("day").toISOString()
                        : null,
                    endDate: dateRange.endDate
                        ? dateRange.endDate.endOf("day").toISOString()
                        : null,
                    projectId: selectedProject._id,
                    parentTaskId: selectedParentTask?._id || "All",
                    userId: selectedUser?._id || "All",
                });

                if (!abortController.signal.aborted) {
                    const reportData = Array.isArray(res.data) ? res.data : [];
                    setData(reportData);
                }
            } catch (error) {
                if (!abortController.signal.aborted) {
                    console.error("Error fetching parent task report:", error);
                    setData([]);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        // Debounce the API call by 1 second
        timeoutId = setTimeout(() => {
            fetchReport();
        }, 1000);

        return () => {
            clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [selectedProject, selectedParentTask, selectedUser, dateRange.startDate, dateRange.endDate]);

    const getAllDatesInRange = (start, end) => {
        const dates = [];
        let current = start.startOf("day");
        const last = end.startOf("day");
        while (current.isBefore(last) || current.isSame(last)) {
            dates.push(current.format("YYYY-MM-DD"));
            current = current.add(1, "day");
        }
        return dates;
    };

    // Update date columns when date range changes
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            setDateColumns(getAllDatesInRange(dateRange.startDate, dateRange.endDate));
        } else {
            setDateColumns([]);
        }
    }, [dateRange]);

    // Prepare table columns
    const columns = [
        {
            id: "parentTaskName",
            label: "Parent Task",
            minWidth: 250,
            maxWidth: 250,
            style: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                lineHeight: "1.5em",
                maxHeight: "3em"
            }
        },
        ...dateColumns.map((day) => {
            const dayLabel = dayjs(day).format("MMM-DD");
            return { id: day, label: dayLabel, isTotal: true, totalType: "time" };
        }),
        {
            id: "Total",
            label: "Total Hours",
            isTotal: true,
            isTotalColumn: true,
            totalType: "time",
        },
    ];

    const transformedData = data.map((parentTask) => {
        const row = { parentTaskName: parentTask.parentTaskName };
        let totalMinutes = 0;

        dateColumns.forEach((day) => {
            const entry = parentTask.dates.find((d) => d.day === day);
            if (entry) {
                const hours = Math.floor(entry.totalHours);
                const mins = Math.round((entry.totalHours % 1) * 100);
                totalMinutes += hours * 60 + mins;

                row[day] = `${hours}.${mins.toString().padStart(2, "0")}`;
            } else {
                row[day] = "0.00";
            }
        });

        const totalHours = Math.floor(totalMinutes / 60);
        const totalMins = totalMinutes % 60;
        row.Total = `${totalHours}.${totalMins.toString().padStart(2, "0")}`;

        return row;
    });

    transformedData.sort((a, b) =>
        a.parentTaskName.localeCompare(b.parentTaskName)
    );

    const tableConfig = {
        isAction: false,
        isEdit: false,
        isMore: false,
        isAdmin: false,
    };

    return (
        <div className="app-container">
            <Grid container spacing={2} mb={2} alignItems="center" flexWrap="nowrap">
                <Grid item sx={{ minWidth: 250 }}>
                    <RangePicker
                        value={[
                            dateRange.startDate ? dayjs(dateRange.startDate) : null,
                            dateRange.endDate ? dayjs(dateRange.endDate) : null,
                        ]}
                        onChange={(dates) => {
                            const [start, end] = dates || [];
                            if (start && end) {
                                setDateRange({
                                    startDate: dayjs(start).startOf("day"),
                                    endDate: dayjs(end).endOf("day"),
                                });
                            } else {
                                const initialMonth = dayjs();
                                setDateRange({
                                    startDate: initialMonth.startOf("month"),
                                    endDate: initialMonth.endOf("month"),
                                });
                            }
                        }}
                        className="datePicker"
                        format={"DD-MM-YYYY"}
                        style={{ width: '100%' }}
                    />
                </Grid>
                <Grid item sx={{ minWidth: 200, flexShrink: 0 }}>
                    <Autocomplete
                        options={projects}
                        getOptionLabel={(p) => p.name || ""}
                        value={selectedProject}
                        onChange={(e, val) => setSelectedProject(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="Project" size="small" />
                        )}
                        sx={{ width: 200 }}
                    />
                </Grid>
                <Grid item sx={{ minWidth: 200, flexShrink: 0 }}>
                    <Autocomplete
                        options={parentTasks}
                        getOptionLabel={(pt) => {
                            if (pt._id === "All") return "All";
                            const projectTypeName = pt.projectType?.name
                                ? ` (${pt.projectType.name})`
                                : "";
                            const title = pt.title ? ` - ${pt.title}` : "";
                            return (pt.code || "Untitled") + title + projectTypeName;
                        }}
                        value={selectedParentTask}
                        onChange={(e, val) => setSelectedParentTask(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="Parent Task" size="small" />
                        )}
                        sx={{ width: 200 }}
                        disabled={!selectedProject}
                        renderOption={(props, option) => {
                            if (option._id === "All") {
                                return <li {...props}>All</li>;
                            }
                            const projectTypeName = option.projectType?.name
                                ? ` (${option.projectType.name})`
                                : "";
                            return (
                                <li {...props}>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                marginRight: "8px",
                                                flex: 1,
                                            }}
                                        >
                                            {option.code} - {option.title}
                                        </span>
                                        <span
                                            style={{
                                                whiteSpace: "nowrap",
                                                fontSize: "0.85em",
                                                color: "text.secondary",
                                                fontWeight: "500",
                                            }}
                                        >
                                            {projectTypeName}
                                        </span>
                                    </div>
                                </li>
                            );
                        }}
                    />
                </Grid>
                <Grid item sx={{ minWidth: 200, flexShrink: 0 }}>
                    <Autocomplete
                        options={users}
                        getOptionLabel={(u) => u.name || ""}
                        value={selectedUser}
                        onChange={(e, val) => setSelectedUser(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="User" size="small" />
                        )}
                        sx={{ width: 200 }}
                    />
                </Grid>
            </Grid>
            <AdvancedTable
                columns={columns}
                data={transformedData}
                tableConfig={tableConfig}
                isPagination={false}
                openLoader={loading}
            />
        </div>
    );
};

export default ParentTaskReport;
